import { useCallback, useMemo, useState } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Image, SearchAds, ShoppingAds, ThumbUpHollow } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { ValueMap } from "../../filters/types";
import { Chip } from "../../filters/ui/Chip";
import { ALL_ADS_TYPES } from "../constants";
import type { AdsType } from "../types";
import { ALL_SOCIAL_SUBCATEGORIES, getPlatformName, type SocialSubcategoryId } from "../utils/getPlatformName";
import { adsTypeTranslationKeyMap } from "../utils/translationKeysConfig";

import { useAdsAlertsFilterStore } from "./useAdsAlertsFilterStore";

const iconProps = {
    size: "sm",
    color: colorsPalettes.carbon[200],
} as const;

const ADS_TYPE_ICONS: Record<AdsType, React.ReactNode> = {
    Display: <Image {...iconProps} />,
    Search: <SearchAds {...iconProps} />,
    Shopping: <ShoppingAds {...iconProps} />,
    Social: <ThumbUpHollow {...iconProps} />,
};

const NON_SOCIAL_ADS_TYPES = ALL_ADS_TYPES.filter((type) => type !== "Social");

const SubcategoryOption = styled(Combobox.Option)`
    padding-left: 36px;
`;

export const AdsTypeFilter = () => {
    const { translate } = useTranslate();
    const selectedAdsTypes = useAdsAlertsFilterStore((state) => state.selectedAdsTypes);
    const selectedSocialSubcategories = useAdsAlertsFilterStore((state) => state.selectedSocialSubcategories);
    const isDefaultAdsTypesSelected = useAdsAlertsFilterStore((state) => state.isDefaultAdsTypesSelected);
    const setFilterSelection = useAdsAlertsFilterStore((state) => state.setFilterSelection);

    const [isOpen, setIsOpen] = useState(false);
    const [tempAdsTypes, setTempAdsTypes] = useState<ValueMap<AdsType>>(new Map());
    const [tempSocialSubs, setTempSocialSubs] = useState<Set<SocialSubcategoryId>>(new Set());

    const displayText = useMemo(() => {
        if (isDefaultAdsTypesSelected) {
            return translate("activation.alerts.filters.ads-type.all");
        }

        const totalSelected = selectedAdsTypes.size + selectedSocialSubcategories.size;
        if (totalSelected === 1) {
            const [firstAd] = selectedAdsTypes.values();
            if (firstAd) {
                return translate("activation.alerts.filters.ads-type.some", { selection: firstAd.label });
            }
            const [firstSub] = selectedSocialSubcategories;
            return translate("activation.alerts.filters.ads-type.some", {
                selection: getPlatformName(firstSub),
            });
        }

        return translate("activation.alerts.filters.ads-type.some", { selection: totalSelected });
    }, [isDefaultAdsTypesSelected, selectedAdsTypes, selectedSocialSubcategories, translate]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            if (open) {
                setTempAdsTypes(new Map(selectedAdsTypes));
                setTempSocialSubs(new Set(selectedSocialSubcategories));
            }
        },
        [selectedAdsTypes, selectedSocialSubcategories],
    );

    const handleAdsTypeToggle = useCallback(
        (type: AdsType) => {
            setTempAdsTypes((prev) => {
                const next = new Map(prev);
                if (next.has(type)) {
                    next.delete(type);
                } else {
                    next.set(type, { id: type, label: translate(adsTypeTranslationKeyMap[type]) });
                }
                return next;
            });
        },
        [translate],
    );

    const handleSocialCategoryToggle = useCallback(() => {
        setTempSocialSubs((prev) => {
            if (prev.size === ALL_SOCIAL_SUBCATEGORIES.length) {
                return new Set();
            }
            return new Set(ALL_SOCIAL_SUBCATEGORIES);
        });
    }, []);

    const handleSocialSubToggle = useCallback((sub: SocialSubcategoryId) => {
        setTempSocialSubs((prev) => {
            const next = new Set(prev);
            if (next.has(sub)) {
                next.delete(sub);
            } else {
                next.add(sub);
            }
            return next;
        });
    }, []);

    const handleApply = useCallback(() => {
        setFilterSelection(tempAdsTypes, tempSocialSubs);
        setIsOpen(false);
    }, [setFilterSelection, tempAdsTypes, tempSocialSubs]);

    const handleReset = useCallback(() => {
        setTempAdsTypes(new Map());
        setTempSocialSubs(new Set());
        setFilterSelection(new Map(), new Set());
    }, [setFilterSelection]);

    const socialAllSelected = tempSocialSubs.size === ALL_SOCIAL_SUBCATEGORIES.length;
    const socialPartiallySelected = tempSocialSubs.size > 0 && !socialAllSelected;

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
        >
            <Combobox.Trigger>
                <Chip
                    isSelected={!isDefaultAdsTypesSelected}
                    onReset={handleReset}
                    data-automation="ads-type-filter"
                >
                    {displayText}
                </Chip>
            </Combobox.Trigger>

            <Combobox.Content>
                {NON_SOCIAL_ADS_TYPES.map((type) => (
                    <Combobox.Option
                        key={type}
                        value={{ id: type }}
                        onClick={() => handleAdsTypeToggle(type)}
                        textValue={type}
                        data-automation={`ads-type-filter-${type.toLowerCase()}`}
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            gap="8px"
                            width="100%"
                        >
                            <Flex
                                alignItems="center"
                                gap="8px"
                            >
                                {ADS_TYPE_ICONS[type]}
                                <Text
                                    fontVariant="listItem1"
                                    color={colorsPalettes.carbon[500]}
                                >
                                    {translate(adsTypeTranslationKeyMap[type])}
                                </Text>
                            </Flex>
                            <Combobox.OptionCheckbox isChecked={tempAdsTypes.has(type)} />
                        </Flex>
                    </Combobox.Option>
                ))}

                <Combobox.Option
                    key="Social"
                    value={{ id: "Social" }}
                    onClick={handleSocialCategoryToggle}
                    textValue="Social"
                    data-automation="ads-type-filter-social"
                >
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap="8px"
                        width="100%"
                    >
                        <Flex
                            alignItems="center"
                            gap="8px"
                        >
                            {ADS_TYPE_ICONS.Social}
                            <Text
                                fontVariant="listItem1"
                                color={colorsPalettes.carbon[500]}
                            >
                                {translate(adsTypeTranslationKeyMap.Social)}
                            </Text>
                        </Flex>
                        <Combobox.OptionCheckbox
                            isChecked={socialAllSelected}
                            halfSelected={socialPartiallySelected}
                        />
                    </Flex>
                </Combobox.Option>

                {ALL_SOCIAL_SUBCATEGORIES.map((sub) => (
                    <SubcategoryOption
                        key={sub}
                        value={{ id: sub }}
                        onClick={() => handleSocialSubToggle(sub)}
                        textValue={sub}
                        data-automation={`ads-type-filter-social-${sub.toLowerCase()}`}
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            gap="8px"
                            width="100%"
                        >
                            <Combobox.OptionLabel>{getPlatformName(sub)}</Combobox.OptionLabel>
                            <Combobox.OptionCheckbox isChecked={tempSocialSubs.has(sub)} />
                        </Flex>
                    </SubcategoryOption>
                ))}
            </Combobox.Content>

            <Combobox.ApplyButton onClick={handleApply}>{translate("common.apply")}</Combobox.ApplyButton>
        </Combobox>
    );
};
